import { useLazyQuery } from '@apollo/client';
import {
  Button,
  Drawer,
  Field,
  Icon,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  PanelRow,
  Pill,
} from '@teamsnap/teamsnap-ui';
import * as GqlOrganizationFormFieldDefinitions from 'core/gql/queries/get-organization-form-field-definitions';
import { capitalize } from 'core/utils';
import * as React from 'react';
import EditAdditionalQuestionForm from './AdditionalQuestionForm';
import AdditionalQuestionModal from './AdditionalQuestionModal';
import './ConfigureQuestionsDrawer.scss';
import { buildFieldFromDefinition } from './Questions';
import { FieldType } from './types';
import WarningModal from './WarningModal';

type ConfigureQuestionsDrawerProps = {
  orgId: number;
  formId: number;
  open: boolean;
  fields: FieldType[];
  onSave: (fields: FieldType[]) => void;
  onClose: Function;
};

const QuestionCategories = {
  participantBasicInfo: 'Participant Information',
  guardianBasicInfo: 'Guardian Information',
  additionalQuestions: 'Additional Questions',
};

const getTypeDisplay = (type: string, isMulti?: boolean) => {
  if (type === 'text') {
    return 'Single line text';
  } else if (type === 'textarea') {
    return 'Multi line text';
  } else if (type === 'select' && !isMulti) {
    return 'Single selection';
  } else if (type === 'select' && isMulti) {
    return 'Multi selection';
  } else if (type === 'file') {
    return 'File Upload';
  }

  return capitalize(type);
};

const getRow = (field: FieldType, fields: FieldType[]) => {
  if (field.target === 'participant' && !field.organization_id) {
    return fields.filter((c) => c.target === 'participant' && !c.organization_id).length;
  } else if (field.target === 'guardian' && !field.organization_id) {
    return fields.filter((c) => c.target === 'guardian' && !c.organization_id).length;
  } else {
    return fields.filter((c) => c.organization_id).length;
  }
};

const updateOrAddField = (fieldToFind, fields, updatedField) => {
  const found = fields.find((field) => fieldToFind.form_field_definition_id === field.form_field_definition_id);
  if (found) {
    return fields.map((field) =>
      field.form_field_definition_id === fieldToFind.form_field_definition_id ? updatedField : field
    );
  } else {
    return [...fields, { ...updatedField, row: getRow(updatedField, fields) }];
  }
};

const ConfigureQuestionsDrawer = ({
  fields: savedFields,
  orgId,
  open,
  onClose,
  onSave,
  formId,
}: ConfigureQuestionsDrawerProps) => {
  const [localSavedFields, setLocalSavedFields] = React.useState<FieldType[]>(savedFields);
  const [drawerFields, setDrawerFields] = React.useState<FieldType[]>([]);
  const [searchValue, setSearchValue] = React.useState<string>(null);
  const [showNewQuestion, setShowNewQuestion] = React.useState(false);
  const [editQuestion, setEditQuestion] = React.useState<FieldType | null>(null);
  const [showWarning, setShowWarning] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);

  const toggleShowNewQuestion = () => setShowNewQuestion(!showNewQuestion);

  const [getFieldDefinitions, { data }] = useLazyQuery(GqlOrganizationFormFieldDefinitions.QUERY, {
    variables: { organization_id: orgId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const mapDrawerQuestions = async (fields, definitions) => {
    const fieldDefinitions = definitions
      ? definitions.organizationFormFieldDefinitions
        .filter((d) => d.active && !d.internal && !(d.external_id || d.external_type))
        .map((d) => {
          const foundField =
            fields.find((f) => f.form_field_definition_id === d.id) ||
            fields.find((f) => f.formFieldDefinitionId === d.id);

          return foundField
            ? {
              ...foundField,
              name: d.name,
              locked: 'locked' in foundField ? foundField.locked : d.locked,
              order: d.order,
              organization_id: d.organization_id,
            }
            : buildFieldFromDefinition(d);
        })
      : [];

    setDrawerFields(fieldDefinitions);

    if (showNewQuestion) {
      const scrollArea = document.querySelector('.question-library .Panel-body');
      scrollArea.scrollTop = scrollArea.scrollHeight;
      setShowNewQuestion(false);
    }
  };

  React.useEffect(() => {
    if (data) {
      mapDrawerQuestions(localSavedFields, data);
    }
  }, [localSavedFields, data]);

  React.useEffect(() => {
    if (savedFields?.length > 0 && drawerFields.length === 0) {
      getFieldDefinitions();
    } else {
      setEditQuestion(null);
    }
  }, [savedFields, drawerFields]);

  const defaultSort = (a: FieldType, b: FieldType) => a.order - b.order;
  const sortByFieldDefinitionId = (a: FieldType, b: FieldType) =>
    a.form_field_definition_id - b.form_field_definition_id;
  const participantQuestions = drawerFields
    .filter((q) => q.target === 'participant' && !q.organization_id)
    .sort(defaultSort);
  const guardianQuestions = drawerFields.filter((q) => q.target === 'guardian' && !q.organization_id).sort(defaultSort);
  const additionalQuestions = drawerFields
    .filter((q) => q.organization_id)
    .sort((q) => q.form_field_definition_id)
    .sort(sortByFieldDefinitionId);

  const closeOrDisplayWarning = () => {
    if (hasChanges) {
      setShowWarning(true);
    } else {
      onClose();
    }
  };

  const QuestionRows = (questions: FieldType[], questionGroupName: string) => (
    <>
      <h3 className="u-spaceBottomMd">{questions?.length > 0 && questionGroupName}</h3>
      <div
        data-testid={`${questionGroupName} Drawer Questions`}
        id={questionGroupName.replace(/ /g, '-').toLowerCase()}
        className="u-spaceBottomMd"
      >
        {questions.map((question, index) => (
          <React.Fragment key={`config-question-${question.form_field_definition_id}-${index}`}>
            <PanelRow mods="Panel-row-question-text">
              <Pill
                testId={`ConfigureQuestionsDrawer--question-${question.form_field_definition_id}`}
                mods="u-flex u-flexAlignItemsCenter u-flexJustifyStart u-size1of1"
              >
                <div className="FieldGroup">
                  <label
                    htmlFor={`${question.name}-${question.form_field_definition_id}`}
                    className="Checkbox Checkbox--inline"
                  >
                    <input
                      id={`${question.name}-${question.form_field_definition_id}`}
                      name={`${question.name}-${question.form_field_definition_id}`}
                      className="Checkbox-input"
                      type="checkbox"
                      checked={question.enabled || question.locked}
                      disabled={question.locked}
                      onChange={() => {
                        const updatedDrawerFields = updateOrAddField(question, drawerFields, {
                          ...question,
                          enabled: !question.enabled,
                        });
                        setDrawerFields(updatedDrawerFields);

                        const updatedLocalSavedFields = updateOrAddField(question, localSavedFields, {
                          ...question,
                          enabled: !question.enabled,
                        });
                        setLocalSavedFields(updatedLocalSavedFields);
                        setHasChanges(true);
                      }}
                    />
                    {<span className="Checkbox-label"></span>}
                  </label>
                </div>
                <div style={{ flex: 1 }}>
                  <p className="u-spaceBottomSm">{question.label}</p>
                  <div className="u-flex">
                    <div className="u-size7of24 u-flex u-flexCol">
                      <p className="u-colorNeutral7">{getTypeDisplay(question.type, question.multiselect)}</p>
                    </div>
                  </div>
                </div>
                <div className="u-flex">
                  <Button type="link" mods="u-colorNeutral8" onClick={() => setEditQuestion(question)}>
                    <Icon name="edit" />
                  </Button>
                </div>
              </Pill>
            </PanelRow>
          </React.Fragment>
        ))}
      </div>
    </>
  );

  return (
    <>
      {open && (
        <div className={`configure-questions-drawer ${editQuestion ? 'edit-question-form' : ''}`}>
          <Drawer
            open={open}
            allowOverlayClose={false}
            closeFn={() => closeOrDisplayWarning()}
            style={{ maxWidth: '730px' }}
            mods="u-sizeFull slide-in"
            overlayMods="fade-in"
            showOverlay={true}
            allowBodyScroll={true}
          >
            <div className={`question-library ${editQuestion ? 'question-library--hidden' : ''}`}>
              <Panel mods="Panel Questions">
                <PanelHeader>
                  <div className="u-flex u-sizeFill u-flexAlignItemsCenter u-flexJustifyBetween u-spaceBottomNone">
                    <h3 data-testid="edit-questions-heading">Edit Questions</h3>
                    <Button
                      type="link"
                      icon="dismiss"
                      mods="u-colorNeutral8 u-padNone u-fontSizeMd"
                      onClick={() => closeOrDisplayWarning()}
                    />
                  </div>
                  <small className="u-flex u-spaceBottomMd">
                    Add, remove, and create questions for this registration form.
                  </small>
                  <Field
                    type="input"
                    name="search"
                    formFieldProps={{
                      placeholder: 'Search question library...',
                      leftIcon: <Icon className="Icon" name="search" />,
                      inputProps: {
                        onChange: ({ target: { value } }) => {
                          setSearchValue(value);
                        },
                        id: 'questionsSearchInput',
                        value: searchValue || '',
                      },
                    }}
                  />
                </PanelHeader>
                <PanelBody>
                  {(!searchValue && (
                    <>
                      {participantQuestions.length > 0 &&
                        QuestionRows(participantQuestions, QuestionCategories.participantBasicInfo)}
                      {guardianQuestions.length > 0 &&
                        QuestionRows(guardianQuestions, QuestionCategories.guardianBasicInfo)}
                      {additionalQuestions.length > 0 &&
                        QuestionRows(additionalQuestions, QuestionCategories.additionalQuestions)}
                    </>
                  )) || (
                      <>
                        {QuestionRows(
                          fuzzySearchQuestions(participantQuestions, searchValue),
                          QuestionCategories.participantBasicInfo
                        )}
                        {QuestionRows(
                          fuzzySearchQuestions(guardianQuestions, searchValue),
                          QuestionCategories.guardianBasicInfo
                        )}
                        {QuestionRows(
                          fuzzySearchQuestions(additionalQuestions, searchValue),
                          QuestionCategories.additionalQuestions
                        )}
                      </>
                    )}
                </PanelBody>
                <PanelFooter>
                  <div className="u-flex u-flexAlignItemsLeft">
                    <Button
                      mods="u-padEndsSm"
                      iconPosition="left"
                      icon="plus"
                      onClick={toggleShowNewQuestion}
                      style={{
                        height: 'auto',
                      }}
                      testId="create-new-question-button"
                    >
                      Create a new question
                    </Button>
                  </div>
                  <div>
                    <Button
                      style={{
                        marginRight: 8,
                      }}
                      onClick={() => closeOrDisplayWarning()}
                      testId="questions-drawer-cancel-button"
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      onClick={() => {
                        onSave(localSavedFields);
                        setHasChanges(false);
                        onClose();
                      }}
                      testId="questions-drawer-save-button"
                    >
                      Save
                    </Button>
                  </div>
                </PanelFooter>
              </Panel>
            </div>
            {editQuestion && (
              <Panel mods="Panel Questions">
                <PanelHeader>
                  <div className="u-flex u-sizeFill u-flexAlignItemsCenter">
                    <Button
                      type="link"
                      icon="arrow-left"
                      mods="back-button u-colorNeutral9 u-spaceRightMd"
                      onClick={() => {
                        setEditQuestion(null);
                      }}
                    />
                    <h3 data-testid="edit-question-heading">Edit Question</h3>
                  </div>
                </PanelHeader>
                <PanelBody>
                  <EditAdditionalQuestionForm
                    orgId={orgId}
                    formId={formId}
                    question={editQuestion}
                    addQuestionToRow={getRow(editQuestion, localSavedFields)}
                    disableEditingAttributes={true}
                    onCancel={() => {
                      setEditQuestion(null);
                    }}
                    onSave={(editedQuestion) => {
                      const updatedDrawerFields = updateOrAddField(editedQuestion, drawerFields, {
                        ...editedQuestion,
                        enabled: true,
                      });
                      setDrawerFields(updatedDrawerFields);

                      const updatedLocalSavedFields = updateOrAddField(editedQuestion, localSavedFields, {
                        ...editedQuestion,
                        enabled: true,
                      });

                      setLocalSavedFields(updatedLocalSavedFields);

                      setHasChanges(true);
                      setEditQuestion(null);

                      const editedElement = document.querySelector(
                        `[data-testid="ConfigureQuestionsDrawer--question-${editedQuestion.form_field_definition_id}"]`
                      );
                      editedElement.classList.add('highlight');

                      setTimeout(() => {
                        editedElement.classList.remove('highlight');
                      }, 1000);
                    }}
                  />
                </PanelBody>
              </Panel>
            )}
          </Drawer>
        </div>
      )}
      {showNewQuestion && (
        <AdditionalQuestionModal
          orgId={orgId}
          formId={formId}
          show={showNewQuestion}
          onClose={toggleShowNewQuestion}
          onSave={(addedQuestion) => {
            setDrawerFields([
              ...drawerFields,
              {
                ...addedQuestion,
                enabled: true,
              },
            ]);

            setLocalSavedFields([
              ...localSavedFields,
              {
                ...addedQuestion,
                enabled: true,
              },
            ]);

            setHasChanges(true);
            toggleShowNewQuestion();

            // Scroll to the end of the list to see newly added question
            const scrollArea = document.querySelector('.question-library .Panel-body');
            scrollArea.scrollTop = scrollArea.scrollHeight;
            const questions = document.querySelectorAll('#additional-questions .Panel-row-question-text');
            const newQuestion = questions[questions.length - 1];
            newQuestion.querySelector('.Pill').classList.add('highlight');
            setTimeout(() => {
              newQuestion.querySelector('.Pill').classList.remove('highlight');
            }, 1000);
          }}
          addQuestionToRow={localSavedFields.filter((c) => c.organization_id).length}
        />
      )}
      {showWarning && (
        <WarningModal
          show={showWarning}
          onClose={() => {
            setShowWarning(false);
          }}
          onSubmit={() => {
            setShowWarning(false);
            onClose();
            mapDrawerQuestions(savedFields, data);
            setHasChanges(false);
          }}
        >
          <p className="u-padEndsSm">Any unsaved changes will be lost. Are you sure you want to leave this page?</p>
        </WarningModal>
      )}
    </>
  );
};

const fuzzySearchQuestions = (questions: FieldType[], searchValue: string): FieldType[] => {
  const lowercaseFilter = searchValue?.toLowerCase();
  return questions?.filter((q) =>
    Object.values(q).reduce((acc, f) => acc || (f?.toString() || '').toLowerCase().includes(lowercaseFilter), false)
  );
};

export default ConfigureQuestionsDrawer;
